/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DialRuleRestrictionsPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Dial Rule Restrictions management with list view.

 🔧 Notes:
    - Refactored from monolithic file
    - Uses hooks for state management
    - Delegates data fetching to useDialRuleRestrictionList

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Edit, Trash2, Copy, Shield } from 'lucide-react';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useDataGrid } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { useDialRuleRestrictionList } from './hooks/useDialRuleRestrictionList';
import {
  deleteDialRuleRestrictionById,
  navigateToDialRuleRestrictionDuplicate,
} from './dialRuleRestrictionsActions';
import { DialRuleRestriction } from './types';

const EMPTY_PLACEHOLDER = '—';

export function DialRuleRestrictionsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(undefined), []);
  const pageTitle = useMemo(() => t('dialRuleRestrictions.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restrictionToDelete, setRestrictionToDelete] = useState<DialRuleRestriction | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { fetchData } = useDialRuleRestrictionList({
    domainId: selectedDomain?.id,
  });

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('dialRuleRestrictions.restrictionName'),
        visible: true,
        sortable: true,
      },
      {
        key: 'description',
        label: t('dialRuleRestrictions.restrictionDescription'),
        visible: true,
        sortable: false,
      },
      { key: 'rules', label: t('dialRuleRestrictions.rules'), visible: true, sortable: false },
      {
        key: 'createdAt',
        label: t('dialRuleRestrictions.createdAt'),
        visible: true,
        sortable: true,
      },
      { key: 'actions', label: t('common.actions'), visible: true, sortable: false },
    ],
    [t]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'dial-rule-restrictions',
  });

  // Refresh data when domain changes
  useEffect(() => {
    if (selectedDomain?.id !== undefined) {
      refresh();
    }
  }, [selectedDomain?.id]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/class-of-service/dial-restriction/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (restriction: DialRuleRestriction) => {
      navigate(`/pbx/class-of-service/dial-restriction/${restriction.uuid || restriction.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (restriction: DialRuleRestriction) => {
      navigateToDialRuleRestrictionDuplicate(navigate, restriction.uuid || restriction.id);
    },
    [navigate]
  );

  const handleDelete = useCallback((restriction: DialRuleRestriction) => {
    setRestrictionToDelete(restriction);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!restrictionToDelete) return;

    try {
      setIsDeleting(true);
      await deleteDialRuleRestrictionById(restrictionToDelete.id);

      setShowDeleteModal(false);
      setRestrictionToDelete(null);

      showSuccess(t('dialRuleRestrictions.messages.deleteSuccess'));

      refresh();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('dialRuleRestrictions.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [restrictionToDelete, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (restriction: DialRuleRestriction) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('dialRuleRestrictions.actions.edit')}>
          <button
            onClick={() => handleEdit(restriction)}
            className="p-1 rounded transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-100/60 dark:text-blue-300 dark:hover:text-blue-200 dark:hover:bg-blue-900/30"
          >
            <Edit className="h-4 w-4" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('dialRuleRestrictions.actions.duplicate')}>
          <button
            onClick={() => handleDuplicate(restriction)}
            className="p-1 rounded transition-colors text-green-600 hover:text-green-700 hover:bg-green-100/60 dark:text-green-300 dark:hover:text-green-200 dark:hover:bg-green-900/30"
          >
            <Copy className="h-4 w-4" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('dialRuleRestrictions.actions.delete')}>
          <button
            onClick={() => handleDelete(restriction)}
            className="p-1 rounded transition-colors text-red-600 hover:text-red-700 hover:bg-red-100/60 dark:text-red-300 dark:hover:text-red-200 dark:hover:bg-red-900/30"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDuplicate, handleDelete, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns.map((column) => {
            if (column.key === 'name') {
              return {
                ...column,
                render: (_value: unknown, item: DialRuleRestriction) => (
                  <span className="font-medium text-foreground">{item.name}</span>
                ),
              };
            }

            if (column.key === 'description') {
              return {
                ...column,
                render: (_value: unknown, item: DialRuleRestriction) => (
                  <span className="text-sm text-muted-foreground truncate block max-w-[320px]">
                    {item.description?.trim() || EMPTY_PLACEHOLDER}
                  </span>
                ),
              };
            }

            if (column.key === 'rules') {
              return {
                ...column,
                render: (_value: unknown, item: DialRuleRestriction) => (
                  <span className="text-sm font-medium">{item._count?.rules ?? 0}</span>
                ),
              };
            }

            if (column.key === 'createdAt') {
              return {
                ...column,
                render: (_value: unknown, item: DialRuleRestriction) => {
                  const date = new Date(item.createdAt);
                  return (
                    <span className="text-sm text-muted-foreground">
                      {!Number.isNaN(date.getTime())
                        ? dateFormatter.format(date)
                        : EMPTY_PLACEHOLDER}
                    </span>
                  );
                },
              };
            }

            if (column.key === 'actions') {
              return {
                ...column,
                render: (_value: unknown, item: DialRuleRestriction) => renderActions(item),
              };
            }

            return column;
          })}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          title={t('dialRuleRestrictions.title')}
          titleIcon={<Shield className="h-5 w-5 text-primary" />}
          addButtonText={t('dialRuleRestrictions.actions.add')}
          searchPlaceholder={t('dialRuleRestrictions.searchPlaceholder')}
          emptyMessage={t('dialRuleRestrictions.empty.description')}
          deleteTitle={t('dialRuleRestrictions.deleteRestriction')}
          deleteMessage={(item) => {
            const name = item?.name ?? '';
            return t('dialRuleRestrictions.messages.confirmDeleteMessage', { name });
          }}
          renderActions={renderActions}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('dialRuleRestrictions.deleteRestriction')}
        message={t('dialRuleRestrictions.messages.confirmDeleteMessage', {
          name: restrictionToDelete?.name ?? '',
        })}
        confirmText={t('dialRuleRestrictions.actions.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

export default DialRuleRestrictionsPage;
